import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Stethoscope, Scissors, Home } from "lucide-react";

const SVC = {
  vet: { icon: Stethoscope, label: "Vet", chip: "bg-sky-500/10 border-sky-500/40 text-sky-700", dot: "bg-sky-500" },
  grooming: { icon: Scissors, label: "Grooming", chip: "bg-violet-500/10 border-violet-500/40 text-violet-700", dot: "bg-violet-500" },
  boarding: { icon: Home, label: "Boarding", chip: "bg-amber-500/10 border-amber-500/40 text-amber-700", dot: "bg-amber-500" },
};
const dayKey = (d) => new Date(d).toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const pad = (n) => String(n).padStart(2, "0");

export function AppointmentCalendar({ items, mode, cursor, setCursor, onSelect, onDropReschedule }) {
  const today = dayKey(new Date());
  const [dragging, setDragging] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const base = new Date(cursor);
  const days = mode === "week"
    ? Array.from({ length: 7 }, (_, i) => { const d = startOfWeek(base); d.setDate(d.getDate() + i); return d; })
    : [new Date(base)];

  const shift = (n) => { const d = new Date(cursor); d.setDate(d.getDate() + n * (mode === "week" ? 7 : 1)); setCursor(d); };

  const byDay = {};
  for (const a of items) (byDay[dayKey(a.date_time)] = byDay[dayKey(a.date_time)] || []).push(a);
  Object.values(byDay).forEach((l) => l.sort((a, b) => new Date(a.date_time) - new Date(b.date_time)));

  const draggable = (a) => a.status !== "confirmed" && a.status !== "cancelled";

  const handleDrop = (d) => {
    setOverKey(null);
    if (!dragging) return;
    const orig = new Date(dragging.date_time);
    const newISO = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(orig.getHours())}:${pad(orig.getMinutes())}`;
    const a = dragging;
    setDragging(null);
    if (dayKey(orig) === dayKey(d)) return; // same day, no change
    onDropReschedule?.(a, newISO);
  };

  const rangeLabel = mode === "week"
    ? `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : base.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <Card className="p-4 border-border/60">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
        </div>
        <div className="font-primary text-lg font-semibold">{rangeLabel}</div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {Object.entries(SVC).map(([k, s]) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />{s.label}</span>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">Tip: drag an appointment onto another day to reschedule it.</p>

      <div className={`grid gap-2 ${mode === "week" ? "grid-cols-2 sm:grid-cols-4 lg:grid-cols-7" : "grid-cols-1"}`}>
        {days.map((d) => {
          const k = dayKey(d);
          const list = byDay[k] || [];
          const isToday = k === today;
          const isOver = overKey === k;
          return (
            <div key={k} data-testid={`cal-day-${k}`}
              onDragOver={(e) => { e.preventDefault(); if (overKey !== k) setOverKey(k); }}
              onDragLeave={() => setOverKey((o) => (o === k ? null : o))}
              onDrop={() => handleDrop(d)}
              className={`rounded-xl border p-2 min-h-[120px] transition-colors ${isOver ? "border-primary bg-primary/10 ring-2 ring-primary/30" : isToday ? "border-primary/50 bg-primary/5" : "border-border/60"}`}>
              <div className={`text-xs font-medium mb-2 flex items-center justify-between ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                <span>{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                <span className="tabular-nums">{d.getDate()}</span>
              </div>
              <div className="space-y-1.5">
                {list.length === 0 && <div className="text-[11px] text-muted-foreground/60 py-2">—</div>}
                {list.map((a) => {
                  const s = SVC[a.service_type] || SVC.vet;
                  const Icon = s.icon;
                  const canDrag = draggable(a);
                  return (
                    <button key={a.id} type="button" onClick={() => onSelect(a)} data-testid={`cal-appt-${a.id}`}
                      draggable={canDrag}
                      onDragStart={(e) => { setDragging(a); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDragging(null); setOverKey(null); }}
                      className={`w-full text-left rounded-lg px-2 py-1.5 text-xs border transition-all ${s.chip} ${canDrag ? "cursor-grab active:cursor-grabbing hover:brightness-95" : "opacity-70"} ${a.status === "confirmed" ? "ring-1 ring-emerald-500/40" : ""}`}>
                      <div className="flex items-center gap-1.5 font-medium">
                        <Icon className="h-3 w-3 shrink-0" />
                        <span className="tabular-nums">{new Date(a.date_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      <div className="truncate mt-0.5">{a.pet_name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
